import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import RequirePermission from '@shared/components/RequirePermission';
import { usePermissions, ClinicMgmtPermissions } from '@shared/context/usePermissions';
import { clinicApi } from '../services/clinicApi';
import type { ClinicDetailDto, UpdateClinicRequest } from '../types/clinic';
import { US_STATES } from '@shared/constants/us-data';
import ClinicMap from './ClinicMap';

import PhoneLink, { PhoneDisplay } from '@shared/components/common/PhoneLink';
import './ClinicInfoSection.css';

interface ClinicInfoSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

interface EditFormState {
  name: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  mailingStreet: string;
  mailingSuite: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
}

function buildFormState(clinic: ClinicDetailDto): EditFormState {
  return {
    name: clinic.name || '',
    phone: clinic.phone || '',
    fax: clinic.fax || '',
    email: clinic.email || '',
    website: clinic.website || '',
    mailingStreet: clinic.mailingStreet || '',
    mailingSuite: clinic.mailingSuite || '',
    mailingCity: clinic.mailingCity || '',
    mailingState: clinic.mailingState || '',
    mailingZip: clinic.mailingZip || '',
  };
}

/** Parse the API 400 response into { summary, fields } */
function parseApiError(err: unknown): { summary: string; fields: Record<string, string> } {
  const e = err as { response?: { data?: Record<string, unknown> } };
  const data = e?.response?.data;
  if (!data) return { summary: 'Failed to save changes. Please check your inputs and try again.', fields: {} };

  // Shape 1: { message: "..." } from exception path
  if (typeof data['message'] === 'string') {
    return { summary: data['message'] as string, fields: {} };
  }

  // Shape 2: ModelState dict { "FieldName": ["msg1", ...], ... }
  const fields: Record<string, string> = {};
  const summaryParts: string[] = [];
  for (const [key, val] of Object.entries(data)) {
    const msgs = Array.isArray(val) ? (val as string[]) : [String(val)];
    const fieldKey = key.toLowerCase();
    fields[fieldKey] = msgs[0];
    summaryParts.push(msgs[0]);
  }
  if (summaryParts.length > 0) {
    return { summary: summaryParts.join('; '), fields };
  }

  return { summary: 'Failed to save changes. Please check your inputs and try again.', fields: {} };
}

export default function ClinicInfoSection({ clinic, onUpdate }: Readonly<ClinicInfoSectionProps>) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => buildFormState(clinic));
  const [showSuccess, setShowSuccess] = useState(false);

  // Sync form with clinic data when clinic prop changes and not editing
  const [prevClinic, setPrevClinic] = useState(clinic);
  if (clinic !== prevClinic) {
    setPrevClinic(clinic);
    if (!isEditing) {
      setForm(buildFormState(clinic));
    }
  }

  // One Source Examinetics Account indicator — stored as the 'one_source_account'
  // capability (resolved by key: ids differ per environment), so this checkbox,
  // the Capabilities section checklist, the matrix "One Source Accounts" bucket,
  // and the capability filter all read and write the same flag.
  const { hasPermission } = usePermissions();
  const canEditCapabilities = hasPermission(ClinicMgmtPermissions.EditClinicCapabilities);

  const { data: capabilityTypes = [] } = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: clinicApi.getCapabilityTypes,
  });
  const oneSourceTypeId = capabilityTypes.find(t => t.key === 'one_source_account')?.id;
  const hasOneSourceAccount =
    oneSourceTypeId != null && (clinic.capabilityTypeIds ?? []).includes(oneSourceTypeId);

  const oneSourceMutation = useMutation({
    // updateCapabilityList replaces the clinic's full capability list, so the
    // toggle must send every current id ± this one — never just the one id.
    mutationFn: (next: boolean) => {
      const current = clinic.capabilityTypeIds ?? [];
      const ids = next
        ? [...current.filter(id => id !== oneSourceTypeId), oneSourceTypeId as number]
        : current.filter(id => id !== oneSourceTypeId);
      return clinicApi.updateCapabilityList(clinic.id, ids);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (request: UpdateClinicRequest) =>
      clinicApi.updateClinic(clinic.id, request),
    onSuccess: () => {
      setIsEditing(false);
      setShowSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    if (!form.name.trim()) return;

    const request: UpdateClinicRequest = {
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      fax: form.fax.trim() || null,
      email: form.email.trim() || null,
      website: form.website.trim() || null,
      mailingStreet: form.mailingStreet.trim() || null,
      mailingSuite: form.mailingSuite.trim() || null,
      mailingCity: form.mailingCity.trim() || null,
      mailingState: form.mailingState || null,
      mailingZip: form.mailingZip.trim() || null,
    };

    updateMutation.mutate(request);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm(buildFormState(clinic));
    updateMutation.reset();
  };

  const handleEdit = () => {
    setForm(buildFormState(clinic));
    updateMutation.reset();
    setIsEditing(true);
  };

  const updateField = (field: keyof EditFormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const { summary: errorSummary, fields: fieldErrors } = updateMutation.isError
    ? parseApiError(updateMutation.error)
    : { summary: '', fields: {} };

  return (
    <div className="info-tab">
      <div className="info-tab-container">
        {/* Section header with edit/save/cancel */}
        <div className="info-section-header">
          <button
            type="button"
            className="info-section-toggle"
            onClick={() => !isEditing && setIsCollapsed(c => !c)}
            aria-expanded={!isCollapsed}
            disabled={isEditing}
          >
            <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
            <h3 className="info-section-title">Clinic Details</h3>
          </button>
          <div className="info-section-actions">
            {showSuccess && (
              <span className="info-success-toast">
                <CheckCircle size={14} />
                Saved
              </span>
            )}
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="svc-header-btn svc-header-btn-cancel"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                >
                  <X size={14} />
                  Cancel
                </button>
                <button
                  type="button"
                  className="svc-header-btn svc-header-btn-save"
                  onClick={handleSave}
                  disabled={updateMutation.isPending || !form.name.trim()}
                >
                  <Save size={14} />
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <RequirePermission permission="UserCanEditClinicProfile">
                <button
                  type="button"
                  className="svc-edit-btn"
                  onClick={handleEdit}
                  title="Edit clinic info"
                >
                  <Pencil size={14} />
                  Edit
                </button>
              </RequirePermission>
            )}
          </div>
        </div>

        {!isCollapsed && (
          <>
            {/* One Source Examinetics Account indicator (Morgan's team, 2026-08-04):
                a clinic holding the general Examinetics account any customer can
                book through. Flag only — no account numbers are stored. */}
            {oneSourceTypeId != null && (
              <label className={`info-onesource-banner${!canEditCapabilities ? ' readonly' : ''}`}>
                <input
                  type="checkbox"
                  checked={hasOneSourceAccount}
                  disabled={!canEditCapabilities || oneSourceMutation.isPending}
                  onChange={e => oneSourceMutation.mutate(e.target.checked)}
                />
                <span className="info-onesource-label">One Source Examinetics Account</span>
                <span className="info-onesource-hint">
                  Clinic holds a general Examinetics account any customer can use
                </span>
                {oneSourceMutation.isPending && (
                  <span className="info-onesource-status">Saving…</span>
                )}
                {oneSourceMutation.isError && (
                  <span className="info-onesource-error">
                    <AlertCircle size={13} />
                    Failed to save — try again
                  </span>
                )}
              </label>
            )}

            {updateMutation.isError && (
              <div className="info-save-error">
                <AlertCircle size={14} />
                {errorSummary}
              </div>
            )}

            <div className="info-content">
              <div className="info-details">
                {/* Row 1: Name. Status is deliberately NOT editable here — the
                    header Activate/Deactivate button owns status changes. The
                    old dropdown wrote Status via a binary isActive flag, which
                    stomped Pending Verification and wrote Draft for "inactive"
                    (SLV2-3195). */}
                {isEditing && (
                  <div className="info-row">
                    <div className="info-field">
                      <label htmlFor="clinic-name">Name</label>
                      <input
                        id="clinic-name"
                        type="text"
                        className={`info-input${fieldErrors['name'] ? ' info-input-error' : ''}`}
                        value={form.name}
                        onChange={e => updateField('name', e.target.value)}
                      />
                      {fieldErrors['name'] && <span className="info-field-error">{fieldErrors['name']}</span>}
                    </div>
                  </div>
                )}

                {/* Row 2: Phone + Fax */}
                <div className="info-row">
                  <div className="info-field">
                    <label htmlFor="clinic-phone">Phone</label>
                    {isEditing ? (
                      <>
                        <input
                          id="clinic-phone"
                          type="tel"
                          className={`info-input${fieldErrors['phone'] ? ' info-input-error' : ''}`}
                          value={form.phone}
                          onChange={e => updateField('phone', e.target.value)}
                          placeholder="(555) 555-5555"
                        />
                        {fieldErrors['phone'] && <span className="info-field-error">{fieldErrors['phone']}</span>}
                      </>
                    ) : (
                      <span>{clinic.phone ? <PhoneLink phone={clinic.phone} /> : 'N/A'}</span>
                    )}
                  </div>
                  <div className="info-field">
                    <label htmlFor="clinic-fax">Fax</label>
                    {isEditing ? (
                      <>
                        <input
                          id="clinic-fax"
                          type="tel"
                          className={`info-input${fieldErrors['fax'] ? ' info-input-error' : ''}`}
                          value={form.fax}
                          onChange={e => updateField('fax', e.target.value)}
                          placeholder="(555) 555-5555"
                        />
                        {fieldErrors['fax'] && <span className="info-field-error">{fieldErrors['fax']}</span>}
                      </>
                    ) : (
                      <span>{clinic.fax ? <PhoneDisplay phone={clinic.fax} /> : 'N/A'}</span>
                    )}
                  </div>
                </div>

                {/* Row 3: Email + Website */}
                <div className="info-row">
                  <div className="info-field">
                    <label htmlFor="clinic-email">Email</label>
                    {isEditing ? (
                      <>
                        <input
                          id="clinic-email"
                          type="email"
                          className={`info-input${fieldErrors['email'] ? ' info-input-error' : ''}`}
                          value={form.email}
                          onChange={e => updateField('email', e.target.value)}
                          placeholder="clinic@example.com"
                        />
                        {fieldErrors['email'] && <span className="info-field-error">{fieldErrors['email']}</span>}
                      </>
                    ) : (
                      <span>{clinic.email || 'N/A'}</span>
                    )}
                  </div>
                  <div className="info-field">
                    <label htmlFor="clinic-website">Website</label>
                    {isEditing ? (
                      <>
                        <input
                          id="clinic-website"
                          type="url"
                          className={`info-input${fieldErrors['website'] ? ' info-input-error' : ''}`}
                          value={form.website}
                          onChange={e => updateField('website', e.target.value)}
                          placeholder="https://..."
                        />
                        {fieldErrors['website'] && <span className="info-field-error">{fieldErrors['website']}</span>}
                      </>
                    ) : (
                      <span>
                        {clinic.website ? (
                          <a href={clinic.website} target="_blank" rel="noopener noreferrer">{clinic.website}</a>
                        ) : 'N/A'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Mailing Address section (edit mode) */}
                {isEditing && (
                  <>
                    <div className="info-address-divider">
                      <span>Mailing Address</span>
                    </div>
                    <div className="info-row">
                      <div className="info-field info-field-wide">
                        <label htmlFor="clinic-street">Street</label>
                        <input
                          id="clinic-street"
                          type="text"
                          className={`info-input${fieldErrors['mailingstreet'] ? ' info-input-error' : ''}`}
                          value={form.mailingStreet}
                          onChange={e => updateField('mailingStreet', e.target.value)}
                        />
                        {fieldErrors['mailingstreet'] && <span className="info-field-error">{fieldErrors['mailingstreet']}</span>}
                      </div>
                      <div className="info-field">
                        <label htmlFor="clinic-suite">Suite</label>
                        <input
                          id="clinic-suite"
                          type="text"
                          className="info-input"
                          value={form.mailingSuite}
                          onChange={e => updateField('mailingSuite', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="info-row">
                      <div className="info-field">
                        <label htmlFor="clinic-city">City</label>
                        <input
                          id="clinic-city"
                          type="text"
                          className={`info-input${fieldErrors['mailingcity'] ? ' info-input-error' : ''}`}
                          value={form.mailingCity}
                          onChange={e => updateField('mailingCity', e.target.value)}
                        />
                        {fieldErrors['mailingcity'] && <span className="info-field-error">{fieldErrors['mailingcity']}</span>}
                      </div>
                      <div className="info-field">
                        <label htmlFor="clinic-state">State</label>
                        <select
                          id="clinic-state"
                          className={`info-select${fieldErrors['mailingstate'] ? ' info-input-error' : ''}`}
                          value={form.mailingState}
                          onChange={e => updateField('mailingState', e.target.value)}
                        >
                          <option value="">Select...</option>
                          {US_STATES.map(s => (
                            <option key={s.value} value={s.value}>{s.value} - {s.label}</option>
                          ))}
                        </select>
                        {fieldErrors['mailingstate'] && <span className="info-field-error">{fieldErrors['mailingstate']}</span>}
                      </div>
                      <div className="info-field">
                        <label htmlFor="clinic-zip">Zip</label>
                        <input
                          id="clinic-zip"
                          type="text"
                          className={`info-input${fieldErrors['mailingzip'] ? ' info-input-error' : ''}`}
                          value={form.mailingZip}
                          onChange={e => updateField('mailingZip', e.target.value)}
                          placeholder="12345"
                          maxLength={10}
                        />
                        {fieldErrors['mailingzip'] && <span className="info-field-error">{fieldErrors['mailingzip']}</span>}
                      </div>
                    </div>
                  </>
                )}

                {/* Address (read-only) */}
                {!isEditing && (
                  <div className="info-row">
                    <div className="info-field">
                      <label>Address</label>
                      <span>{clinic.fullAddress || 'N/A'}</span>
                    </div>
                  </div>
                )}

                {/* Row: Last Verified */}
                {!isEditing && (
                  <div className="info-row">
                    <div className="info-field">
                      <label>Last Verified</label>
                      <span>
                        {clinic.lastVerifiedDate
                          ? `${new Date(clinic.lastVerifiedDate).toLocaleDateString()}${clinic.lastVerifiedByUserName ? ` by ${clinic.lastVerifiedByUserName}` : ''}`
                          : 'Never verified'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Row: Created + Modified (always read-only) */}
                <div className="info-row">
                  <div className="info-field">
                    <label>Created</label>
                    <span>{clinic.createdDate ? new Date(clinic.createdDate).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="info-field">
                    <label>Last Modified</label>
                    <span>{clinic.modifiedDate ? new Date(clinic.modifiedDate).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Map (hide during edit for less clutter) */}
              {!isEditing && clinic.fullAddress && (
                <div className="info-map">
                  <ClinicMap address={clinic.fullAddress} clinicName={clinic.name} />
                </div>
              )}
            </div>

          </>
        )}
      </div>
    </div>
  );
}
